import { combineLatest, defer, distinctUntilChanged, filter, map, of, type Observable } from 'rxjs';
import type {
    CellRenderOptionsDeps,
    RuntimeResultLinkDescriptorTabular,
    RuntimeTabularCellRenderOptions,
    RuntimeTabularColumnMetadata,
} from './types';
import { getDataType } from '../../../../../graph/data-types';
import type { DataSet } from '../../../../../graph/dataset';
import { language$ } from '../../../../../utils/date-fns';
import { getNamingConventionsForGuideline } from '../../../../../utils/naming-conventions';
import type { Profile } from '../../../../../profile';

/**
 * The guideline driving naming conventions, from the measuring point's own config rather than
 * the process-global `guideline$`, which only the edit form pushes and is stale elsewhere.
 * Only `VibrationConfig` has one, so `null` for sound and air is an answer, not an omission.
 */
function guideLine$(dataSet: DataSet) {
    return dataSet.config$.pipe(
        // `undefined` means the config has not resolved yet, so wait rather than fail. A
        // `null` config means resolution failed, which is a genuine error.
        filter((config): config is Exclude<typeof config, undefined> => config !== undefined),
        map((config) => {
            if (!config || config.__typename !== 'VibrationConfig') {
                return null;
            }

            if (!config.guideLine) {
                throw new Error(
                    'Missing guideline on the vibration measuring point config while resolving table naming conventions.'
                );
            }

            return config.guideLine;
        }),
        distinctUntilChanged()
    );
}

/**
 * Naming conventions for a table, reactive to both inputs that can change them: the
 * measuring point's guideline and the active language. The two existing paths each covered
 * only one — the global `namingConventions$` is language-reactive but config-blind, while
 * `getNamingConventionsForGuideline` is config-reactive but language-blind.
 */
function namingConventionsForDataSet$(dataSet: DataSet): Observable<Record<string, string>> {
    return combineLatest([guideLine$(dataSet), language$]).pipe(
        map(([guideLine]) => (guideLine ? getNamingConventionsForGuideline(guideLine) : {}))
    );
}

/**
 * Resolved unit per data type. `getUnit$` already unifies the two unit sources: the base
 * implementation applies the profile's distance unit, and the sound data types override it to
 * read the frequency weighting from the DataSet config.
 */
function unitsForDataTypes$(
    dataTypes: readonly string[],
    dataSet: DataSet,
    profile: Profile
): Observable<Record<string, string | null>> {
    if (dataTypes.length === 0) {
        return of({});
    }

    return combineLatest(
        dataTypes.map((dataType) => getDataType(dataType).getUnit$({ dataSet, profile }))
    ).pipe(
        map((units) =>
            Object.fromEntries(dataTypes.map((dataType, index) => [dataType, units[index]]))
        )
    );
}

function measurementDataTypes(columns: readonly RuntimeTabularColumnMetadata[]): readonly string[] {
    return Array.from(
        new Set(
            columns.flatMap((column) => (column.kind === 'measurement' ? [column.dataType] : []))
        )
    );
}

function columnRequiresTimezone(column: RuntimeTabularColumnMetadata): boolean {
    return (
        column.kind === 'timestamp' ||
        column.kind === 'timeRange' ||
        (column.kind === 'text' && column.requiresTimezone === true)
    );
}

/** Whether a link needs a DataSet for measurement formatting or time rendering. */
export function linkNeedsDataSet(
    link: Pick<RuntimeResultLinkDescriptorTabular, 'columns'>
): boolean {
    return (
        link.columns.some(columnRequiresTimezone) || measurementDataTypes(link.columns).length > 0
    );
}

export function resolveCellRenderOptions$(
    link: Pick<RuntimeResultLinkDescriptorTabular, 'key' | 'columns'>,
    deps: CellRenderOptionsDeps,
    profile: Profile
): Observable<RuntimeTabularCellRenderOptions> {
    const dataTypes = measurementDataTypes(link.columns);

    if (!linkNeedsDataSet(link)) {
        // Nothing here depends on a DataSet, so the profile is the whole contract and the
        // remaining fields are trivially empty.
        return of({ profile, namingConventions: {}, units: {} });
    }

    return defer(() => {
        const dataSet = deps.getDataSet();
        return combineLatest([
            link.columns.some(columnRequiresTimezone)
                ? dataSet.timezone$.pipe(map((timezone) => ({ timezone })))
                : of({}),
            namingConventionsForDataSet$(dataSet),
            unitsForDataTypes$(dataTypes, dataSet, profile),
        ]).pipe(
            map(([timezoneOptions, namingConventions, units]) => ({
                ...timezoneOptions,
                profile,
                namingConventions,
                units,
            }))
        );
    });
}
